/**
 * OCR fallback for scanned PDFs via Gemini multimodal.
 *
 * pdf-parse only handles text PDFs — Saudi government tenders are often
 * scanned (image-based PDFs) and yield empty / garbled output. When that
 * happens we ship the raw PDF bytes to Gemini, which can read both text
 * and images inside PDFs natively, and ask it to OCR the content (Arabic
 * + English).
 */

const GEMINI_OCR_MODEL = "gemini-2.5-flash";

// PDFs larger than ~18 MB cannot be sent inline; we fall back to truncating
// or skipping. For the typical Saudi tender (<30 MB) this is fine.
const MAX_INLINE_PDF_BYTES = 18 * 1024 * 1024;

export interface OcrResult {
  text: string;
  source: "gemini-multimodal";
  warning?: string;
}

/**
 * Extract text from a (possibly scanned) PDF using Gemini multimodal.
 * Returns null when the API key is missing or the API call fails so
 * the caller can fall back to whatever they had.
 */
export async function extractTextFromPdfWithGemini(
  pdfBuffer: Buffer,
  opts: { language?: "ar" | "en" } = {},
): Promise<OcrResult | null> {
  const apiKey = process.env.GEMINI_API_KEY;
  if (!apiKey) return null;
  if (pdfBuffer.length > MAX_INLINE_PDF_BYTES) {
    return {
      text: "",
      source: "gemini-multimodal",
      warning: `PDF too large for inline OCR (${(pdfBuffer.length / 1024 / 1024).toFixed(1)} MB > ${MAX_INLINE_PDF_BYTES / 1024 / 1024} MB)`,
    };
  }

  const isAr = opts.language !== "en";
  const prompt = isAr
    ? "هذا ملف PDF يحتوي على مستند مناقصة (قد يكون نصياً أو ممسوحاً ضوئياً). استخرج كل النص الموجود فيه حرفياً بالعربية والإنجليزية، مع الحفاظ على ترتيب الأقسام، العناوين والجداول قدر الإمكان. لا تضف أي تعليق أو ملخص. أعد النص الخام فقط."
    : "This PDF is a tender document (may be text or scanned). Extract every piece of text inside it verbatim, in Arabic and English, preserving section order, headings and tables as much as possible. Do not summarise or comment. Return raw text only.";

  const url = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_OCR_MODEL}:generateContent?key=${apiKey}`;
  const body = {
    contents: [
      {
        role: "user",
        parts: [
          { text: prompt },
          {
            inlineData: {
              mimeType: "application/pdf",
              data: pdfBuffer.toString("base64"),
            },
          },
        ],
      },
    ],
    generationConfig: { temperature: 0.0, maxOutputTokens: 8192 },
  };

  try {
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      return {
        text: "",
        source: "gemini-multimodal",
        warning: `Gemini OCR HTTP ${res.status}: ${errText.slice(0, 200)}`,
      };
    }
    const data = (await res.json()) as any;
    const text = (data?.candidates?.[0]?.content?.parts ?? [])
      .map((p: any) => p?.text ?? "")
      .join("")
      .trim();
    return { text, source: "gemini-multimodal" };
  } catch (err: any) {
    return {
      text: "",
      source: "gemini-multimodal",
      warning: `Gemini OCR error: ${String(err?.message ?? err).slice(0, 200)}`,
    };
  }
}

/**
 * Decide whether a previously-extracted text string is so thin it should
 * be re-processed via OCR. Tuned for ~A4 PDFs: < 200 chars per page is
 * almost certainly a scanned document.
 */
export function shouldRunOcrFallback(
  extracted: string | null | undefined,
  pdfSizeBytes: number,
): boolean {
  const text = (extracted ?? "").trim();
  if (!text) return true;
  // Rough heuristic: scanned PDFs average ~30-80 KB per page; if the
  // extracted text is < 0.5% the size of the file, treat as scanned.
  const ratio = text.length / Math.max(pdfSizeBytes, 1);
  if (ratio < 0.005 && text.length < 1500) return true;
  if (text.length < 300) return true;
  return false;
}
